import React, { useMemo } from "react";
import AsyncSelect from "react-select/async";
import { api } from "../../api";
import "../../sass/ui/selectCliente.scss";

/* Helpers */
const digits = (v = "") => (v || "").replace(/\D+/g, "");
const labelize = (f = {}) => [f.Nome, f.Usuario, f.CPF].filter(Boolean).join(" • ");

const debounce = (fn, delay = 350) => {
  let t;
  return (...args) =>
    new Promise((resolve) => {
      clearTimeout(t);
      t = setTimeout(async () => resolve(await fn(...args)), delay);
    });
};

/* Data fetch */
async function fetchFuncionarios(termo, { situacao, nivel, includeBloqueados, pageSize }) {
  // Lookup direto por ID numérico curto
  if (/^\d{1,10}$/.test(termo)) {
    try {
      const { data } = await api.get(`/func/${termo}`);
      return data?.IdFuncionario ? [{ value: data, label: labelize(data) }] : [];
    } catch {
      return [];
    }
  }

  // Busca paginada / fuzzy
  try {
    const params = {
      page: 1,
      limit: pageSize || 10,
      q: termo,
    };
    if (situacao) params.situacao = situacao;

    // garante que "0" não seja descartado
    if (nivel !== undefined && nivel !== null && String(nivel).length > 0) {
      params.nivel = String(nivel).toUpperCase();
    }

    if (includeBloqueados === false) params.bloqueado = 0;

    const { data } = await api.get("/func", { params });
    const arr = Array.isArray(data?.data) ? data.data : [];
    return arr.map((f) => ({ value: f, label: labelize(f) }));
  } catch {
    return [];
  }
}

/* Componente */
const SelectFuncionario = ({
  onChange,
  value = null,
  className = "select-func",
  placeholder = "Busque por nome, usuário, CPF ou ID",
  minChars = 2,
  pageSize = 10,
  situacao = undefined,
  nivel = undefined,
  includeBloqueados = false,
  defaultOptions = false,
  disabled = false,
}) => {
  const load = useMemo(
    () =>
      debounce(async (inputValue) => {
        const termo = String(inputValue || "").trim();
        if (termo.length < minChars) return [];
        return fetchFuncionarios(termo, { situacao, nivel, includeBloqueados, pageSize });
      }, 350),
    [situacao, nivel, includeBloqueados, pageSize, minChars]
  );

  const selectedOption = useMemo(
    () => (value ? { value, label: labelize(value) } : null),
    [value]
  );

  return (
    <AsyncSelect
      className={className}
      classNamePrefix="react-select"
      cacheOptions
      loadOptions={load}
      value={selectedOption}
      onChange={(option) => onChange(option?.value || null)}
      getOptionValue={(opt) =>
        String(opt?.value?.IdFuncionario ?? opt?.value?.id ?? "")
      }
      placeholder={placeholder}
      isClearable
      defaultOptions={defaultOptions}
      isDisabled={disabled}
      noOptionsMessage={() => "Nenhum resultado"}
      loadingMessage={() => "Carregando..."}
      // evita filtro client-side extra; servidor já filtrou
      filterOption={null}
    />
  );
};

export default SelectFuncionario;
